import React, { useState } from 'react';
import { useLogin, useNotify, Notification } from 'react-admin';
import { Box } from '@mui/material';
import {
    Logo,
    TextField,
    PasswordField,
    ButtonField,
    PasswordFieldMode,
} from '@wk/components-v3-react16';
import { useT } from '../i18n/useT';

export const LoginPage: React.FC = () => {
    const login = useLogin();
    const notify = useNotify();
    const t = useT();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [mode, setMode] = useState<PasswordFieldMode>('password');
    const [loading, setLoading] = useState(false);

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setLoading(true);
        login({ username, password }).catch(() => {
            notify(t('auth.login.invalidCredentials'), { type: 'error' });
            setLoading(false);
        });
    };

    return (
        <Box
            className="cg3-typography"
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                background:
                    'radial-gradient(circle at top left, rgba(22, 50, 79, 0.12), transparent 36%),' +
                    'radial-gradient(circle at bottom right, rgba(181, 109, 43, 0.12), transparent 36%),' +
                    'linear-gradient(180deg, #f5f1ea 0%, #f8f7f4 100%)',
            }}
        >
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    width: '100%',
                    maxWidth: 420,
                    backgroundColor: 'var(--cg3-color-monochrome-white, #fff)',
                    borderRadius: 'var(--cg3-border-radius-double, 8px)',
                    boxShadow: 'var(--cg3-box-shadow-raised, 0 8px 28px rgba(22,50,79,0.16))',
                    border: '1px solid var(--cg3-border-color, rgba(22,50,79,0.1))',
                    p: { xs: 3, sm: 4 },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--cg3-spacing, 16px)',
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <Logo size="medium" alt="Wolters Kluwer" />
                </Box>

                <Box sx={{ textAlign: 'center', mb: 1 }}>
                    <h1
                        style={{
                            margin: 0,
                            fontSize: 'var(--cg3-h4-font-size, 1.5rem)',
                            color: 'var(--cg3-color-text-primary, #16324f)',
                        }}
                    >
                        {t('app.brand')}
                    </h1>
                    <p
                        style={{
                            margin: '4px 0 0',
                            color: 'var(--cg3-color-text-secondary, #5a6b7b)',
                            fontSize: 'var(--cg3-font-size-base, 0.875rem)',
                        }}
                    >
                        {t('auth.login.subtitle')}
                    </p>
                </Box>

                <TextField label={t('auth.login.email')} labelFor="login-username" labelId="login-username-label">
                    <input
                        id="login-username"
                        type="text"
                        name="username"
                        value={username}
                        onChange={event => setUsername(event.target.value)}
                        autoComplete="username"
                        autoFocus
                    />
                </TextField>

                <PasswordField
                    label={t('auth.login.password')}
                    labelFor="login-password"
                    labelId="login-password-label"
                    mode={mode}
                    onModeChange={({ mode: nextMode }) => setMode(nextMode)}
                >
                    <input
                        id="login-password"
                        type={mode}
                        name="password"
                        value={password}
                        onChange={event => setPassword(event.target.value)}
                        autoComplete="current-password"
                    />
                </PasswordField>

                <Box
                    sx={{
                        mt: 1,
                        display: 'flex',
                        '& > *': { flex: 1 },
                        '& button': { width: '100%' },
                    }}
                >
                    <ButtonField type="primary" size="large">
                        <button type="submit" disabled={loading || !username || !password}>
                            {loading ? t('auth.login.submitting') : t('auth.login.submit')}
                        </button>
                    </ButtonField>
                </Box>
            </Box>

            <Notification />
        </Box>
    );
};

export default LoginPage;
